// ANETT HORVATH — Brandhouse Data Store
// Client-side persistent data layer with full CRUD, tier engine, audit logging, and seed data.
// All data persists in localStorage. Structured for future backend migration.

import { create } from 'zustand';
import { TIER_ORDER, TIER_THRESHOLDS, GRACE_PERIOD_DAYS, MEMBERSHIP_YEAR_DAYS, INVITE_ELIGIBLE_TIERS, LOGIN_MAX_ATTEMPTS, LOGIN_WINDOW_MINUTES, LOCK_DURATION_MINUTES } from '../config/house.config';
import type { Tier, SupportedLang } from '../config/house.config';
import type { User, Member, Order, Release, ArchiveItem, HouseMessage, HouseEvent, Invite, SystemEvent, MemberActivityLog, HouseCategory, MediaAsset, LegalDocument, LegalAcceptance, AdminAuditLog, FailedJob } from '../types';

const DB_KEY = 'ah_brandhouse_db';
const AUTH_KEY = 'ah_auth_session';
const uid = (): string => {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch { /* non-secure context */ }
  return 'id_' + Math.random().toString(36).slice(2) + '_' + Date.now();
};
const now = () => new Date().toISOString();
const hashPw = (p: string) => btoa(p); // Simple encoding for demo; real backend uses bcrypt
const checkPw = (p: string, h: string) => btoa(p) === h;
const genMemberCode = () => 'AH-' + Array.from({ length: 6 }, () => 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'[Math.floor(Math.random() * 36)]).join('');
const genInviteCode = () => 'AH-INV-' + Array.from({ length: 5 }, () => 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'[Math.floor(Math.random() * 36)]).join('');
const genOrderNumber = () => 'ORD-' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).substring(2, 5).toUpperCase();

interface DB {
  users: User[]; members: Member[]; orders: Order[]; releases: Release[];
  archive_items: ArchiveItem[]; house_messages: HouseMessage[]; events: HouseEvent[];
  invites: Invite[]; system_events: SystemEvent[]; member_activity_log: MemberActivityLog[];
  house_categories: HouseCategory[]; media_assets: MediaAsset[]; legal_documents: LegalDocument[];
  legal_acceptance: LegalAcceptance[]; admin_audit_log: AdminAuditLog[]; failed_jobs: FailedJob[];
  login_attempts: Record<string, { count: number; first_at: number; locked_until?: number }>;
}

interface AuthSession { user_id: string; role: string; member_id?: string; }

interface StoreState extends DB {
  session: AuthSession | null;
  _initialized: boolean;
  // Init
  init: () => void;
  // Auth
  login: (email: string, password: string) => { ok: boolean; error?: string };
  register: (email: string, password: string, firstName: string, country: string, lang: SupportedLang, inviteCode?: string) => { ok: boolean; error?: string };
  logout: () => void;
  getUser: () => User | null;
  getMember: () => Member | null;
  isAdmin: () => boolean;
  hasRole: (roles: string[]) => boolean;
  // Legal
  getPendingLegal: (memberId: string) => LegalDocument[];
  acceptLegal: (memberId: string, docType: string, version: string) => void;
  // Member CRUD (admin)
  approveMember: (memberId: string, adminId: string) => void;
  pauseMember: (memberId: string, adminId: string) => void;
  revokeMember: (memberId: string, adminId: string) => void;
  reactivateMember: (memberId: string, adminId: string) => void;
  overrideTier: (memberId: string, tier: Tier, adminId: string) => void;
  updateMemberLang: (memberId: string, lang: SupportedLang) => void;
  // Orders
  createOrder: (data: { member_id: string; amount_eur: number; source: string; release_id?: string; house_category_slug?: string }, adminId?: string) => Order;
  payOrder: (orderId: string, adminId?: string) => void;
  refundOrder: (orderId: string, adminId: string) => void;
  // Releases CRUD
  upsertRelease: (data: Partial<Release> & { id?: string }, adminId: string) => Release;
  deleteRelease: (id: string, adminId: string) => void;
  // Archive CRUD
  upsertArchiveItem: (data: Partial<ArchiveItem> & { id?: string }, adminId: string) => ArchiveItem;
  deleteArchiveItem: (id: string, adminId: string) => void;
  // Messages CRUD
  upsertMessage: (data: Partial<HouseMessage> & { id?: string }, adminId: string) => HouseMessage;
  deleteMessage: (id: string, adminId: string) => void;
  // Events CRUD
  upsertEvent: (data: Partial<HouseEvent> & { id?: string }, adminId: string) => HouseEvent;
  deleteEvent: (id: string, adminId: string) => void;
  // Categories CRUD
  upsertCategory: (data: Partial<HouseCategory> & { id?: string }, adminId: string) => HouseCategory;
  // Invites
  createInvite: (memberId: string, maxUses: number, expiresInDays: number) => Invite | null;
  // Legal docs (admin)
  upsertLegalDoc: (data: Partial<LegalDocument> & { id?: string }, adminId: string) => LegalDocument;
  // Tier engine
  recalcTier: (memberId: string) => void;
  // Dashboard overview
  getOverview: (memberId: string, lang: SupportedLang) => any;
  // Scheduler
  runScheduler: () => void;
  // Helpers
  _persist: () => void;
  _audit: (adminId: string, action: string, targetType: string, targetId: string, before: any, after: any) => void;
  _log: (memberId: string, action: string, details?: any) => void;
  _sysEvent: (type: string, payload: any) => void;
}

function emptyDB(): DB {
  return {
    users: [], members: [], orders: [], releases: [], archive_items: [],
    house_messages: [], events: [], invites: [], system_events: [],
    member_activity_log: [], house_categories: [], media_assets: [],
    legal_documents: [], legal_acceptance: [], admin_audit_log: [],
    failed_jobs: [], login_attempts: {},
  };
}

function seedData(): DB {
  const db = emptyDB();
  const ts = now();
  // Admin user
  const adminId = uid();
  db.users.push({ id: adminId, email: 'admin@anetthorvath.com', password_hash: hashPw('House2026!'), role: 'super_admin', preferred_language: 'en', created_at: ts, deleted_at: null });
  // Demo member user
  const memUserId = uid();
  db.users.push({ id: memUserId, email: 'member@example.com', password_hash: hashPw('Member2026!'), role: 'member', preferred_language: 'en', created_at: ts, deleted_at: null });
  const memId = uid();
  const yearStart = ts;
  const yearEnd = new Date(Date.now() + MEMBERSHIP_YEAR_DAYS * 86400000).toISOString();
  db.members.push({ id: memId, user_id: memUserId, first_name: 'Alexandra', country: 'Hungary', member_code: genMemberCode(), status: 'active', membership_state: 'active', access_tier: 'INNER_CIRCLE', yearly_spend_eur: 4200, lifetime_spend_eur: 4200, membership_year_start: yearStart, membership_year_end: yearEnd, approved_at: ts, black_card_candidate: false, grace_period_until: null, marketing_consent: true, notes: '', created_at: ts, updated_at: ts, deleted_at: null });

  // Categories
  const cats = [
    { slug: 'parfum', sort: 1, names: { en: { name: 'Parfum' }, hu: { name: 'Parfüm' }, de: { name: 'Parfum' }, it: { name: 'Profumo' }, fr: { name: 'Parfum' } } },
    { slug: 'footwear-heels', sort: 2, names: { en: { name: 'Footwear — Heels' }, hu: { name: 'Lábbeli — Magassarkú' }, de: { name: 'Schuhe — Absätze' }, it: { name: 'Calzature — Tacchi' }, fr: { name: 'Chaussures — Talons' } } },
    { slug: 'jewelry', sort: 3, names: { en: { name: 'Jewelry' }, hu: { name: 'Ékszer' }, de: { name: 'Schmuck' }, it: { name: 'Gioielli' }, fr: { name: 'Bijoux' } } },
    { slug: 'scented-objects', sort: 4, names: { en: { name: 'Scented Objects' }, hu: { name: 'Illatosított tárgyak' }, de: { name: 'Duftobjekte' }, it: { name: 'Oggetti profumati' }, fr: { name: 'Objets parfumés' } } },
  ];
  cats.forEach(c => db.house_categories.push({ id: uid(), slug: c.slug, status: 'active', sort_order: c.sort, translations: c.names, created_at: ts, updated_at: ts, deleted_at: null }));

  // Releases
  const relData = [
    { slug: 'prototype-01', status: 'live_private' as const, cat: 'footwear-heels', en: { title: 'Prototype 01', content: '85 mm heel · Black calf leather · Deep Burgundy sole. The first expression of the House.' }, hu: { title: 'Prototípus 01', content: '85 mm sarok · Fekete borjúbőr · Mély bordó talp. A Ház első megnyilvánulása.' }, de: { title: 'Prototyp 01', content: '85 mm Absatz · Schwarzes Kalbsleder · Tiefburgunder Sohle. Der erste Ausdruck des Hauses.' }, it: { title: 'Prototipo 01', content: '85 mm tacco · Pelle di vitello nera · Suola bordeaux. La prima espressione della Casa.' }, fr: { title: 'Prototype 01', content: '85 mm talon · Cuir de veau noir · Semelle bordeaux. La première expression de la Maison.' }, preview: ['INITIATE','INNER_CIRCLE','FOUNDING_CIRCLE','COLLECTOR','BLACK_CARD'] as Tier[], eligible: ['INNER_CIRCLE','FOUNDING_CIRCLE','COLLECTOR','BLACK_CARD'] as Tier[] },
    { slug: 'scent-01-still', status: 'upcoming' as const, cat: 'parfum', en: { title: 'Scent 01 — Still', content: 'A fragrance of restraint. Notes undisclosed.' }, hu: { title: 'Illat 01 — Csend', content: 'A visszafogottság illata. Jegyek nem nyilvánosak.' }, de: { title: 'Duft 01 — Stille', content: 'Ein Duft der Zurückhaltung. Noten nicht offengelegt.' }, it: { title: 'Profumo 01 — Quiete', content: 'Un profumo di moderazione. Note non divulgate.' }, fr: { title: 'Parfum 01 — Silence', content: 'Un parfum de retenue. Notes non divulguées.' }, preview: ['INITIATE','INNER_CIRCLE','FOUNDING_CIRCLE','COLLECTOR','BLACK_CARD'] as Tier[], eligible: ['FOUNDING_CIRCLE','COLLECTOR','BLACK_CARD'] as Tier[] },
    { slug: 'heel-02-nocturne', status: 'live_private' as const, cat: 'footwear-heels', en: { title: 'Heel 02 — Nocturne', content: '100 mm stiletto · Midnight suede · Signature sole.' }, hu: { title: 'Sarok 02 — Nocturne', content: '100 mm tűsarok · Éjfél velúr · Jellegzetes talp.' }, de: { title: 'Absatz 02 — Nocturne', content: '100 mm Stiletto · Mitternacht-Wildleder · Signatur-Sohle.' }, it: { title: 'Tacco 02 — Notturno', content: '100 mm stiletto · Pelle scamosciata · Suola firma.' }, fr: { title: 'Talon 02 — Nocturne', content: '100 mm stiletto · Daim minuit · Semelle signature.' }, preview: ['INNER_CIRCLE','FOUNDING_CIRCLE','COLLECTOR','BLACK_CARD'] as Tier[], eligible: ['FOUNDING_CIRCLE','COLLECTOR','BLACK_CARD'] as Tier[] },
    { slug: 'ring-01-signet', status: 'live_public' as const, cat: 'jewelry', en: { title: 'Ring 01 — Signet', content: 'House symbol rendered in 18k gold. Numbered edition.' }, hu: { title: 'Gyűrű 01 — Pecsét', content: 'A Ház szimbóluma 18k aranyban. Számozott kiadás.' }, de: { title: 'Ring 01 — Siegel', content: 'Haussymbol in 18k Gold. Nummerierte Auflage.' }, it: { title: 'Anello 01 — Sigillo', content: 'Simbolo della Casa in oro 18k. Edizione numerata.' }, fr: { title: 'Bague 01 — Sceau', content: 'Symbole de la Maison en or 18k. Édition numérotée.' }, preview: ['INITIATE','INNER_CIRCLE','FOUNDING_CIRCLE','COLLECTOR','BLACK_CARD'] as Tier[], eligible: ['INNER_CIRCLE','FOUNDING_CIRCLE','COLLECTOR','BLACK_CARD'] as Tier[] },
    { slug: 'candle-01-maison', status: 'closed' as const, cat: 'scented-objects', en: { title: 'Candle 01 — Maison', content: 'Hand-poured. 220g. Scent profile: private.' }, hu: { title: 'Gyertya 01 — Maison', content: 'Kézzel öntött. 220g. Illatprofil: privát.' }, de: { title: 'Kerze 01 — Maison', content: 'Handgegossen. 220g. Duftprofil: privat.' }, it: { title: 'Candela 01 — Maison', content: 'Colata a mano. 220g. Profilo olfattivo: privato.' }, fr: { title: 'Bougie 01 — Maison', content: 'Coulée à la main. 220g. Profil olfactif: privé.' }, preview: ['INITIATE','INNER_CIRCLE','FOUNDING_CIRCLE','COLLECTOR','BLACK_CARD'] as Tier[], eligible: ['INITIATE','INNER_CIRCLE','FOUNDING_CIRCLE','COLLECTOR','BLACK_CARD'] as Tier[] },
  ];
  const wsNow = new Date();
  relData.forEach((r, i) => {
    const ws = new Date(wsNow.getTime() - (i * 30 * 86400000));
    const we = new Date(ws.getTime() + 60 * 86400000);
    db.releases.push({ id: uid(), slug: r.slug, translations: { en: r.en, hu: r.hu, de: r.de, it: r.it, fr: r.fr }, status: r.status, house_category_slug: r.cat, cover_media_id: null, preview_tiers: r.preview, eligible_tiers: r.eligible, window_start: ws.toISOString(), window_end: we.toISOString(), created_at: ts, updated_at: ts, deleted_at: null });
  });

  // Archive items
  const archData = [
    { slug: 'the-first-sketch', cat: 'footwear-heels', tier: 'INITIATE' as Tier, en: { title: 'The First Sketch', content: 'Where the House began. A single line on paper.' }, hu: { title: 'Az első vázlat', content: 'Ahol a Ház elkezdődött. Egyetlen vonal papíron.' }, de: { title: 'Die erste Skizze', content: 'Wo das Haus begann. Eine einzelne Linie.' }, it: { title: 'Il primo schizzo', content: 'Dove la Casa è iniziata. Una singola linea.' }, fr: { title: 'Le premier croquis', content: 'Où la Maison a commencé. Une seule ligne.' } },
    { slug: 'sole-development', cat: 'footwear-heels', tier: 'INNER_CIRCLE' as Tier, en: { title: 'Sole Development', content: 'The journey to the signature deep burgundy.' }, hu: { title: 'Talp fejlesztés', content: 'Az út a jellegzetes mély bordóig.' }, de: { title: 'Sohlenentwicklung', content: 'Der Weg zum charakteristischen Burgunder.' }, it: { title: 'Sviluppo suola', content: 'Il percorso verso il bordeaux firma.' }, fr: { title: 'Développement de semelle', content: 'Le chemin vers le bordeaux signature.' } },
    { slug: 'monogram-origins', cat: 'jewelry', tier: 'INITIATE' as Tier, en: { title: 'Monogram Origins', content: 'How AH became a symbol.' }, hu: { title: 'A monogram eredete', content: 'Hogyan lett az AH szimbólum.' }, de: { title: 'Monogramm Ursprünge', content: 'Wie AH zum Symbol wurde.' }, it: { title: 'Origini del monogramma', content: 'Come AH è diventato un simbolo.' }, fr: { title: 'Origines du monogramme', content: 'Comment AH est devenu un symbole.' } },
    { slug: 'leather-selection', cat: 'footwear-heels', tier: 'FOUNDING_CIRCLE' as Tier, en: { title: 'Leather Selection', content: 'The process of choosing the perfect calf leather.' }, hu: { title: 'Bőr kiválasztás', content: 'A tökéletes borjúbőr kiválasztásának folyamata.' }, de: { title: 'Lederauswahl', content: 'Der Prozess der perfekten Kalbslederauswahl.' }, it: { title: 'Selezione pelle', content: 'Il processo di selezione del vitello perfetto.' }, fr: { title: 'Sélection du cuir', content: 'Le processus de choix du cuir parfait.' } },
    { slug: 'scent-laboratory', cat: 'parfum', tier: 'INNER_CIRCLE' as Tier, en: { title: 'The Scent Laboratory', content: 'Inside the private fragrance development space.' }, hu: { title: 'Az illatlabor', content: 'A privát illatfejlesztő tér belseje.' }, de: { title: 'Das Duftlabor', content: 'Im privaten Duftentwicklungsraum.' }, it: { title: 'Il laboratorio olfattivo', content: 'Dentro lo spazio di sviluppo fragranze.' }, fr: { title: 'Le laboratoire olfactif', content: "À l'intérieur de l'espace de développement." } },
    { slug: 'house-symbol-evolution', cat: 'jewelry', tier: 'COLLECTOR' as Tier, en: { title: 'House Symbol Evolution', content: 'Five iterations before the final form.' }, hu: { title: 'A Ház szimbólum evolúciója', content: 'Öt iteráció a végleges formáig.' }, de: { title: 'Haussymbol Evolution', content: 'Fünf Iterationen bis zur endgültigen Form.' }, it: { title: 'Evoluzione del simbolo', content: 'Cinque iterazioni prima della forma finale.' }, fr: { title: 'Évolution du symbole', content: 'Cinq itérations avant la forme finale.' } },
  ];
  archData.forEach(a => db.archive_items.push({ id: uid(), slug: a.slug, translations: { en: a.en, hu: a.hu, de: a.de, it: a.it, fr: a.fr }, house_category_slug: a.cat, cover_media_id: null, min_tier: a.tier, created_at: ts, updated_at: ts, deleted_at: null }));

  // Messages
  const msgData = [
    { slug: 'welcome-to-the-house', pub: true, en: { title: 'Welcome to the House', content: 'You have entered a space built on intention. What follows will be revealed with care.' }, hu: { title: 'Üdvözöljük a Házban', content: 'Szándékra épített térbe lépett. Ami következik, gondosan kerül feltárásra.' }, de: { title: 'Willkommen im Haus', content: 'Sie haben einen Raum betreten, der auf Absicht aufgebaut ist.' }, it: { title: 'Benvenuto nella Casa', content: 'Siete entrati in uno spazio costruito con intenzione.' }, fr: { title: 'Bienvenue dans la Maison', content: 'Vous êtes entré dans un espace construit avec intention.' } },
    { slug: 'prototype-01-announcement', pub: true, en: { title: 'Prototype 01 — Now Available', content: 'The first expression of the House is ready for Inner Circle and above.' }, hu: { title: 'Prototípus 01 — Elérhető', content: 'A Ház első megnyilvánulása Inner Circle és felette elérhető.' }, de: { title: 'Prototyp 01 — Verfügbar', content: 'Der erste Ausdruck des Hauses ist bereit.' }, it: { title: 'Prototipo 01 — Disponibile', content: 'La prima espressione della Casa è pronta.' }, fr: { title: 'Prototype 01 — Disponible', content: 'La première expression de la Maison est prête.' } },
    { slug: 'spring-2026-preview', pub: false, en: { title: 'Spring 2026 Preview', content: 'A private preview for select members. Details to follow.' }, hu: { title: 'Tavasz 2026 előzetes', content: 'Privát előzetes kiválasztott tagoknak.' }, de: { title: 'Frühjahr 2026 Vorschau', content: 'Eine private Vorschau für ausgewählte Mitglieder.' }, it: { title: 'Anteprima Primavera 2026', content: "Un'anteprima privata per membri selezionati." }, fr: { title: 'Aperçu Printemps 2026', content: 'Un aperçu privé pour les membres sélectionnés.' } },
  ];
  msgData.forEach(m => db.house_messages.push({ id: uid(), slug: m.slug, translations: { en: m.en, hu: m.hu, de: m.de, it: m.it, fr: m.fr }, cover_media_id: null, is_published: m.pub, published_at: m.pub ? ts : null, created_at: ts, updated_at: ts, deleted_at: null }));

  // Events
  const evData = [
    { slug: 'private-viewing-budapest', en: { title: 'Private Viewing — Budapest', content: 'An intimate presentation of the first collection.' }, hu: { title: 'Privát bemutató — Budapest', content: 'Az első kollekció intim bemutatója.' }, de: { title: 'Private Besichtigung — Budapest', content: 'Eine intime Präsentation der ersten Kollektion.' }, it: { title: 'Visione privata — Budapest', content: 'Una presentazione intima della prima collezione.' }, fr: { title: 'Présentation privée — Budapest', content: 'Une présentation intime de la première collection.' }, date: new Date(Date.now() + 45 * 86400000).toISOString(), loc: 'Budapest, Hungary', tier: 'FOUNDING_CIRCLE' as Tier },
    { slug: 'house-dinner-vienna', en: { title: 'House Dinner — Vienna', content: 'An evening with the founder. By invitation only.' }, hu: { title: 'Ház vacsora — Bécs', content: 'Egy este az alapítóval. Csak meghívóval.' }, de: { title: 'Hausdinner — Wien', content: 'Ein Abend mit der Gründerin. Nur auf Einladung.' }, it: { title: 'Cena della Casa — Vienna', content: 'Una serata con la fondatrice. Solo su invito.' }, fr: { title: 'Dîner de la Maison — Vienne', content: 'Une soirée avec la fondatrice. Sur invitation uniquement.' }, date: new Date(Date.now() + 90 * 86400000).toISOString(), loc: 'Vienna, Austria', tier: 'COLLECTOR' as Tier },
  ];
  evData.forEach(e => db.events.push({ id: uid(), slug: e.slug, translations: { en: e.en, hu: e.hu, de: e.de, it: e.it, fr: e.fr }, event_date: e.date, location: e.loc, min_tier: e.tier, is_published: true, created_at: ts, updated_at: ts, deleted_at: null }));

  // Legal documents
  ['terms', 'privacy', 'cookies'].forEach(dt => {
    db.legal_documents.push({ id: uid(), document_type: dt as any, version: '1.0', content_url: `#legal-${dt}-v1`, effective_at: ts, is_active: true, created_at: ts });
  });
  // Auto-accept legal for seeded member
  db.legal_documents.forEach(ld => {
    db.legal_acceptance.push({ id: uid(), member_id: memId, document_type: ld.document_type, version: ld.version, accepted_at: ts, ip_address: '127.0.0.1' });
  });

  return db;
}

export const useStore = create<StoreState>((set, get) => ({
  ...emptyDB(),
  session: null,
  _initialized: false,

  init: () => {
    if (get()._initialized) return;
    let data: DB;
    try {
      const raw = localStorage.getItem(DB_KEY);
      data = raw ? JSON.parse(raw) : seedData();
      if (!raw) {
        try { localStorage.setItem(DB_KEY, JSON.stringify(data)); } catch { /* storage full / blocked */ }
      }
    } catch (e) {
      console.error('[AH] DB load failed, falling back to seed data:', e);
      try { data = seedData(); } catch (e2) { console.error('[AH] seedData also failed, using empty DB:', e2); data = emptyDB(); }
    }
    let session: AuthSession | null = null;
    try {
      const s = localStorage.getItem(AUTH_KEY);
      if (s) session = JSON.parse(s);
    } catch { /* */ }
    set({ ...data, session, _initialized: true });
    // Run scheduler on init
    try { setTimeout(() => get().runScheduler(), 500); } catch { /* */ }
  },

  _persist: () => {
    const s = get();
    const data: DB = {
      users: s.users, members: s.members, orders: s.orders, releases: s.releases,
      archive_items: s.archive_items, house_messages: s.house_messages, events: s.events,
      invites: s.invites, system_events: s.system_events, member_activity_log: s.member_activity_log,
      house_categories: s.house_categories, media_assets: s.media_assets, legal_documents: s.legal_documents,
      legal_acceptance: s.legal_acceptance, admin_audit_log: s.admin_audit_log, failed_jobs: s.failed_jobs,
      login_attempts: s.login_attempts,
    };
    localStorage.setItem(DB_KEY, JSON.stringify(data));
  },

  _audit: (adminId, action, targetType, targetId, before, after) => {
    const entry: AdminAuditLog = { id: uid(), admin_user_id: adminId, action, target_type: targetType, target_id: targetId, before_state: before, after_state: after, ip_address: '127.0.0.1', created_at: now() };
    set(s => ({ admin_audit_log: [...s.admin_audit_log, entry] }));
  },

  _log: (memberId, action, details = {}) => {
    const entry: MemberActivityLog = { id: uid(), member_id: memberId, action, details, created_at: now() };
    set(s => ({ member_activity_log: [...s.member_activity_log, entry] }));
  },

  _sysEvent: (type, payload) => {
    const ev: SystemEvent = { id: uid(), event_type: type, payload, created_at: now(), processed_at: now(), retry_count: 0, last_retry_at: null, failed_reason: null };
    set(s => ({ system_events: [...s.system_events, ev] }));
  },

  // AUTH
  login: (email, password) => {
    const s = get();
    const attempts = s.login_attempts[email] || { count: 0, first_at: Date.now() };
    if (attempts.locked_until && Date.now() < attempts.locked_until) return { ok: false, error: 'auth.locked' };
    if (attempts.count >= LOGIN_MAX_ATTEMPTS && Date.now() - attempts.first_at < LOGIN_WINDOW_MINUTES * 60000) {
      const la = { ...s.login_attempts, [email]: { ...attempts, locked_until: Date.now() + LOCK_DURATION_MINUTES * 60000 } };
      set({ login_attempts: la }); get()._persist();
      return { ok: false, error: 'auth.locked' };
    }
    const user = s.users.find(u => u.email === email && !u.deleted_at);
    if (!user || !checkPw(password, user.password_hash)) {
      const la = { ...s.login_attempts, [email]: { count: (attempts.count || 0) + 1, first_at: attempts.count === 0 ? Date.now() : attempts.first_at } };
      set({ login_attempts: la }); get()._persist();
      return { ok: false, error: 'auth.invalid_credentials' };
    }
    const member = s.members.find(m => m.user_id === user.id && !m.deleted_at);
    const session: AuthSession = { user_id: user.id, role: user.role, member_id: member?.id };
    localStorage.setItem(AUTH_KEY, JSON.stringify(session));
    const la = { ...s.login_attempts }; delete la[email];
    set({ session, login_attempts: la });
    if (member) get()._log(member.id, 'login');
    get()._persist();
    return { ok: true };
  },

  register: (email, password, firstName, country, lang, inviteCode) => {
    const s = get();
    if (s.users.find(u => u.email === email && !u.deleted_at)) return { ok: false, error: 'auth.email_exists' };
    if (inviteCode) {
      const inv = s.invites.find(i => i.code === inviteCode && i.status === 'active' && !i.deleted_at);
      if (!inv) return { ok: false, error: 'auth.invalid_invite' };
      if (inv.used_count >= inv.max_uses) return { ok: false, error: 'auth.invite_exhausted' };
      if (new Date(inv.expires_at) < new Date()) return { ok: false, error: 'auth.invite_expired' };
      set(s => ({ invites: s.invites.map(i => i.id === inv.id ? { ...i, used_count: i.used_count + 1, status: i.used_count + 1 >= i.max_uses ? 'consumed' as const : i.status } : i) }));
    }
    const userId = uid();
    const user: User = { id: userId, email, password_hash: hashPw(password), role: 'member', preferred_language: lang, created_at: now(), deleted_at: null };
    const memId = uid();
    const member: Member = { id: memId, user_id: userId, first_name: firstName, country, member_code: genMemberCode(), status: 'waitlist', membership_state: 'inactive', access_tier: 'INITIATE', yearly_spend_eur: 0, lifetime_spend_eur: 0, membership_year_start: null, membership_year_end: null, approved_at: null, black_card_candidate: false, grace_period_until: null, marketing_consent: false, notes: '', created_at: now(), updated_at: now(), deleted_at: null };
    set(s => ({ users: [...s.users, user], members: [...s.members, member] }));
    const session: AuthSession = { user_id: userId, role: 'member', member_id: memId };
    localStorage.setItem(AUTH_KEY, JSON.stringify(session));
    set({ session });
    get()._sysEvent('member_registered', { member_id: memId, email });
    get()._persist();
    return { ok: true };
  },

  logout: () => {
    localStorage.removeItem(AUTH_KEY);
    set({ session: null });
  },

  getUser: () => {
    const s = get();
    if (!s.session) return null;
    return s.users.find(u => u.id === s.session!.user_id) || null;
  },

  getMember: () => {
    const s = get();
    if (!s.session?.member_id) return null;
    return s.members.find(m => m.id === s.session!.member_id && !m.deleted_at) || null;
  },

  isAdmin: () => {
    const s = get();
    return !!s.session && ['super_admin', 'house_admin', 'editor', 'support'].includes(s.session.role);
  },

  hasRole: (roles) => {
    const s = get();
    return !!s.session && roles.includes(s.session.role);
  },

  // LEGAL
  getPendingLegal: (memberId) => {
    const s = get();
    const activeDocs = s.legal_documents.filter(d => d.is_active);
    return activeDocs.filter(d => !s.legal_acceptance.find(a => a.member_id === memberId && a.document_type === d.document_type && a.version === d.version));
  },

  acceptLegal: (memberId, docType, version) => {
    const entry: LegalAcceptance = { id: uid(), member_id: memberId, document_type: docType as any, version, accepted_at: now(), ip_address: '127.0.0.1' };
    set(s => ({ legal_acceptance: [...s.legal_acceptance, entry] }));
    get()._log(memberId, 'legal_accepted', { docType, version });
    get()._persist();
  },

  // MEMBER ADMIN
  approveMember: (memberId, adminId) => {
    const before = get().members.find(m => m.id === memberId);
    const ts2 = now();
    set(s => ({ members: s.members.map(m => m.id === memberId ? { ...m, status: 'active' as const, membership_state: 'active' as const, approved_at: ts2, membership_year_start: ts2, membership_year_end: new Date(Date.now() + MEMBERSHIP_YEAR_DAYS * 86400000).toISOString(), updated_at: ts2 } : m) }));
    get()._audit(adminId, 'member_approved', 'member', memberId, { status: before?.status }, { status: 'active' });
    get()._log(memberId, 'member_approved');
    get()._sysEvent('member_approved', { member_id: memberId });
    get()._persist();
  },

  pauseMember: (memberId, adminId) => {
    const before = get().members.find(m => m.id === memberId);
    set(s => ({ members: s.members.map(m => m.id === memberId ? { ...m, status: 'paused' as const, updated_at: now() } : m) }));
    get()._audit(adminId, 'member_paused', 'member', memberId, { status: before?.status }, { status: 'paused' });
    get()._log(memberId, 'status_changed', { to: 'paused' });
    get()._persist();
  },

  revokeMember: (memberId, adminId) => {
    const before = get().members.find(m => m.id === memberId);
    set(s => ({ members: s.members.map(m => m.id === memberId ? { ...m, status: 'revoked' as const, updated_at: now() } : m) }));
    get()._audit(adminId, 'member_revoked', 'member', memberId, { status: before?.status }, { status: 'revoked' });
    get()._log(memberId, 'status_changed', { to: 'revoked' });
    get()._persist();
  },

  reactivateMember: (memberId, adminId) => {
    const before = get().members.find(m => m.id === memberId);
    set(s => ({ members: s.members.map(m => m.id === memberId ? { ...m, status: 'active' as const, membership_state: 'active' as const, updated_at: now() } : m) }));
    get()._audit(adminId, 'member_reactivated', 'member', memberId, { status: before?.status }, { status: 'active' });
    get()._log(memberId, 'status_changed', { to: 'active' });
    get()._persist();
  },

  overrideTier: (memberId, tier, adminId) => {
    const before = get().members.find(m => m.id === memberId);
    set(s => ({ members: s.members.map(m => m.id === memberId ? { ...m, access_tier: tier, updated_at: now() } : m) }));
    get()._audit(adminId, 'tier_override', 'member', memberId, { tier: before?.access_tier }, { tier });
    get()._log(memberId, 'tier_changed', { to: tier, method: 'admin_override' });
    get()._persist();
  },

  updateMemberLang: (memberId, lang) => {
    const member = get().members.find(m => m.id === memberId);
    if (!member) return;
    set(s => ({ users: s.users.map(u => u.id === member.user_id ? { ...u, preferred_language: lang } : u) }));
    get()._persist();
  },

  // ORDERS
  createOrder: (data, adminId) => {
    const order: Order = { id: uid(), order_number: genOrderNumber(), member_id: data.member_id, amount_eur: data.amount_eur, currency: 'EUR', status: 'pending', source: data.source as any, external_id: null, source_event_id: null, currency_original: null, amount_original: null, fx_rate: null, eligible_for_tier_progression: true, release_id: data.release_id || null, house_category_slug: data.house_category_slug || null, created_at: now(), paid_at: null, deleted_at: null };
    set(s => ({ orders: [...s.orders, order] }));
    if (adminId) get()._audit(adminId, 'order_created', 'order', order.id, null, { amount: data.amount_eur, source: data.source });
    get()._log(data.member_id, 'order_created', { order_id: order.id });
    get()._persist();
    return order;
  },

  payOrder: (orderId, adminId) => {
    const s = get();
    const order = s.orders.find(o => o.id === orderId);
    if (!order || order.status !== 'pending') return;
    set(s => ({ orders: s.orders.map(o => o.id === orderId ? { ...o, status: 'paid' as const, paid_at: now() } : o) }));
    if (order.eligible_for_tier_progression) {
      set(s => ({ members: s.members.map(m => m.id === order.member_id ? { ...m, yearly_spend_eur: m.yearly_spend_eur + order.amount_eur, lifetime_spend_eur: m.lifetime_spend_eur + order.amount_eur, updated_at: now() } : m) }));
      get().recalcTier(order.member_id);
    }
    if (adminId) get()._audit(adminId, 'order_paid', 'order', orderId, { status: 'pending' }, { status: 'paid' });
    get()._log(order.member_id, 'order_paid', { order_id: orderId, amount: order.amount_eur });
    get()._sysEvent('order_paid', { order_id: orderId, member_id: order.member_id });
    get()._persist();
  },

  refundOrder: (orderId, adminId) => {
    const s = get();
    const order = s.orders.find(o => o.id === orderId);
    if (!order || order.status !== 'paid') return;
    set(s => ({ orders: s.orders.map(o => o.id === orderId ? { ...o, status: 'refunded' as const, eligible_for_tier_progression: false } : o) }));
    if (order.eligible_for_tier_progression) {
      set(s => ({ members: s.members.map(m => m.id === order.member_id ? { ...m, yearly_spend_eur: Math.max(0, m.yearly_spend_eur - order.amount_eur), lifetime_spend_eur: Math.max(0, m.lifetime_spend_eur - order.amount_eur), updated_at: now() } : m) }));
      get().recalcTier(order.member_id);
    }
    get()._audit(adminId, 'order_refunded', 'order', orderId, { status: 'paid' }, { status: 'refunded' });
    get()._persist();
  },

  // RELEASES
  upsertRelease: (data, adminId) => {
    if (data.id) {
      const before = get().releases.find(r => r.id === data.id);
      set(s => ({ releases: s.releases.map(r => r.id === data.id ? { ...r, ...data, updated_at: now() } as Release : r) }));
      get()._audit(adminId, 'release_updated', 'release', data.id, before, data);
      get()._persist();
      return get().releases.find(r => r.id === data.id)!;
    }
    const rel: Release = { id: uid(), slug: data.slug || '', translations: data.translations || { en: { title: '', content: '' }, hu: { title: '', content: '' }, de: { title: '', content: '' }, it: { title: '', content: '' }, fr: { title: '', content: '' } }, status: data.status || 'upcoming', house_category_slug: data.house_category_slug || '', cover_media_id: null, preview_tiers: data.preview_tiers || TIER_ORDER.slice() as any, eligible_tiers: data.eligible_tiers || [], window_start: data.window_start || null, window_end: data.window_end || null, created_at: now(), updated_at: now(), deleted_at: null };
    set(s => ({ releases: [...s.releases, rel] }));
    get()._audit(adminId, 'release_created', 'release', rel.id, null, rel);
    get()._persist();
    return rel;
  },

  deleteRelease: (id, adminId) => {
    set(s => ({ releases: s.releases.map(r => r.id === id ? { ...r, deleted_at: now() } : r) }));
    get()._audit(adminId, 'release_deleted', 'release', id, null, null);
    get()._persist();
  },

  upsertArchiveItem: (data, adminId) => {
    if (data.id) {
      set(s => ({ archive_items: s.archive_items.map(a => a.id === data.id ? { ...a, ...data, updated_at: now() } as ArchiveItem : a) }));
      get()._audit(adminId, 'archive_updated', 'archive_item', data.id, null, data);
      get()._persist();
      return get().archive_items.find(a => a.id === data.id)!;
    }
    const item: ArchiveItem = { id: uid(), slug: data.slug || '', translations: data.translations || { en: { title: '', content: '' }, hu: { title: '', content: '' }, de: { title: '', content: '' }, it: { title: '', content: '' }, fr: { title: '', content: '' } }, house_category_slug: data.house_category_slug || '', cover_media_id: null, min_tier: data.min_tier || 'INITIATE', created_at: now(), updated_at: now(), deleted_at: null };
    set(s => ({ archive_items: [...s.archive_items, item] }));
    get()._audit(adminId, 'archive_created', 'archive_item', item.id, null, item);
    get()._persist();
    return item;
  },

  deleteArchiveItem: (id, adminId) => {
    set(s => ({ archive_items: s.archive_items.map(a => a.id === id ? { ...a, deleted_at: now() } : a) }));
    get()._audit(adminId, 'archive_deleted', 'archive_item', id, null, null);
    get()._persist();
  },

  upsertMessage: (data, adminId) => {
    if (data.id) {
      set(s => ({ house_messages: s.house_messages.map(m => m.id === data.id ? { ...m, ...data, updated_at: now() } as HouseMessage : m) }));
      get()._audit(adminId, 'message_updated', 'house_message', data.id, null, data);
      get()._persist();
      return get().house_messages.find(m => m.id === data.id)!;
    }
    const msg: HouseMessage = { id: uid(), slug: data.slug || '', translations: data.translations || { en: { title: '', content: '' }, hu: { title: '', content: '' }, de: { title: '', content: '' }, it: { title: '', content: '' }, fr: { title: '', content: '' } }, cover_media_id: null, is_published: false, published_at: null, created_at: now(), updated_at: now(), deleted_at: null };
    set(s => ({ house_messages: [...s.house_messages, msg] }));
    get()._audit(adminId, 'message_created', 'house_message', msg.id, null, msg);
    get()._persist();
    return msg;
  },

  deleteMessage: (id, adminId) => {
    set(s => ({ house_messages: s.house_messages.map(m => m.id === id ? { ...m, deleted_at: now() } : m) }));
    get()._audit(adminId, 'message_deleted', 'house_message', id, null, null);
    get()._persist();
  },

  upsertEvent: (data, adminId) => {
    if (data.id) {
      set(s => ({ events: s.events.map(e => e.id === data.id ? { ...e, ...data, updated_at: now() } as HouseEvent : e) }));
      get()._audit(adminId, 'event_updated', 'event', data.id, null, data);
      get()._persist();
      return get().events.find(e => e.id === data.id)!;
    }
    const ev: HouseEvent = { id: uid(), slug: data.slug || '', translations: data.translations || { en: { title: '', content: '' }, hu: { title: '', content: '' }, de: { title: '', content: '' }, it: { title: '', content: '' }, fr: { title: '', content: '' } }, event_date: data.event_date || now(), location: data.location || '', min_tier: data.min_tier || 'INITIATE', is_published: false, created_at: now(), updated_at: now(), deleted_at: null };
    set(s => ({ events: [...s.events, ev] }));
    get()._audit(adminId, 'event_created', 'event', ev.id, null, ev);
    get()._persist();
    return ev;
  },

  deleteEvent: (id, adminId) => {
    set(s => ({ events: s.events.map(e => e.id === id ? { ...e, deleted_at: now() } : e) }));
    get()._audit(adminId, 'event_deleted', 'event', id, null, null);
    get()._persist();
  },

  upsertCategory: (data, adminId) => {
    if (data.id) {
      set(s => ({ house_categories: s.house_categories.map(c => c.id === data.id ? { ...c, ...data, updated_at: now() } as HouseCategory : c) }));
      get()._audit(adminId, 'category_updated', 'house_category', data.id, null, data);
      get()._persist();
      return get().house_categories.find(c => c.id === data.id)!;
    }
    const cat: HouseCategory = { id: uid(), slug: data.slug || '', status: 'active', sort_order: data.sort_order || 0, translations: data.translations || { en: { name: '' }, hu: { name: '' }, de: { name: '' }, it: { name: '' }, fr: { name: '' } }, created_at: now(), updated_at: now(), deleted_at: null };
    set(s => ({ house_categories: [...s.house_categories, cat] }));
    get()._audit(adminId, 'category_created', 'house_category', cat.id, null, cat);
    get()._persist();
    return cat;
  },

  createInvite: (memberId, maxUses, expiresInDays) => {
    const member = get().members.find(m => m.id === memberId);
    if (!member || !INVITE_ELIGIBLE_TIERS.includes(member.access_tier)) return null;
    const inv: Invite = { id: uid(), code: genInviteCode(), issued_by_member_id: memberId, max_uses: maxUses, used_count: 0, expires_at: new Date(Date.now() + expiresInDays * 86400000).toISOString(), status: 'active', created_at: now(), deleted_at: null };
    set(s => ({ invites: [...s.invites, inv] }));
    get()._log(memberId, 'invite_created', { code: inv.code });
    get()._persist();
    return inv;
  },

  upsertLegalDoc: (data, adminId) => {
    if (data.id) {
      set(s => ({ legal_documents: s.legal_documents.map(d => d.id === data.id ? { ...d, ...data } as LegalDocument : d) }));
      get()._audit(adminId, 'legal_updated', 'legal_document', data.id, null, data);
      get()._persist();
      return get().legal_documents.find(d => d.id === data.id)!;
    }
    // Deactivate previous of same type
    if (data.document_type) {
      set(s => ({ legal_documents: s.legal_documents.map(d => d.document_type === data.document_type ? { ...d, is_active: false } : d) }));
    }
    const doc: LegalDocument = { id: uid(), document_type: data.document_type || 'terms', version: data.version || '1.0', content_url: data.content_url || '#', effective_at: data.effective_at || now(), is_active: true, created_at: now() };
    set(s => ({ legal_documents: [...s.legal_documents, doc] }));
    get()._audit(adminId, 'legal_created', 'legal_document', doc.id, null, doc);
    get()._sysEvent('legal_version_activated', { type: doc.document_type, version: doc.version });
    get()._persist();
    return doc;
  },

  // TIER ENGINE
  recalcTier: (memberId) => {
    const member = get().members.find(m => m.id === memberId);
    if (!member) return;
    const spend = member.yearly_spend_eur;
    let newTier: Tier = 'INITIATE';
    for (const t of [...TIER_ORDER].reverse()) {
      if (t === 'BLACK_CARD') continue; // BLACK_CARD requires admin approval
      if (spend >= TIER_THRESHOLDS[t]) { newTier = t; break; }
    }
    // Check BLACK_CARD candidacy
    const isCandidate = spend >= TIER_THRESHOLDS.BLACK_CARD;
    if (member.access_tier !== newTier || member.black_card_candidate !== isCandidate) {
      set(s => ({ members: s.members.map(m => m.id === memberId ? { ...m, access_tier: newTier, black_card_candidate: isCandidate, updated_at: now() } : m) }));
      if (member.access_tier !== newTier) get()._log(memberId, 'tier_changed', { from: member.access_tier, to: newTier });
      get()._persist();
    }
  },

  // DASHBOARD OVERVIEW (single aggregated endpoint)
  getOverview: (memberId, lang) => {
    const s = get();
    const member = s.members.find(m => m.id === memberId && !m.deleted_at);
    if (!member) return null;
    const tierIdx = TIER_ORDER.indexOf(member.access_tier);
    const latestMsg = s.house_messages.filter(m => m.is_published && !m.deleted_at).sort((a, b) => b.published_at!.localeCompare(a.published_at!))[0];
    const upcomingRel = s.releases.filter(r => r.status === 'upcoming' && !r.deleted_at && r.preview_tiers.some(t => TIER_ORDER.indexOf(t) <= tierIdx))[0];
    const liveReleases = s.releases.filter(r => (r.status === 'live_private' || r.status === 'live_public') && !r.deleted_at).slice(0, 3);
    const archiveItems = s.archive_items.filter(a => !a.deleted_at).slice(0, 6);
    const events = s.events.filter(e => e.is_published && !e.deleted_at && new Date(e.event_date) > new Date()).slice(0, 2);
    const invites = INVITE_ELIGIBLE_TIERS.includes(member.access_tier) ? s.invites.filter(i => i.issued_by_member_id === memberId && !i.deleted_at) : [];
    return { member, latestMsg, upcomingRel, liveReleases, archiveItems, events, invites, lang };
  },

  // SCHEDULER
  runScheduler: () => {
    const s = get();
    const n = new Date();
    // Check membership cycles
    s.members.filter(m => m.status === 'active' && m.membership_year_end && !m.deleted_at).forEach(m => {
      if (new Date(m.membership_year_end!) < n) {
        set(st => ({ members: st.members.map(mem => mem.id === m.id ? { ...mem, yearly_spend_eur: 0, membership_year_start: n.toISOString(), membership_year_end: new Date(n.getTime() + MEMBERSHIP_YEAR_DAYS * 86400000).toISOString(), updated_at: n.toISOString() } : mem) }));
        get()._sysEvent('membership_year_reset', { member_id: m.id });
        get().recalcTier(m.id);
      }
    });
    // Grace period monitor
    s.members.filter(m => m.membership_state === 'grace_period' && m.grace_period_until && !m.deleted_at).forEach(m => {
      if (new Date(m.grace_period_until!) < n) {
        get().recalcTier(m.id);
        set(st => ({ members: st.members.map(mem => mem.id === m.id ? { ...mem, membership_state: 'active' as const, grace_period_until: null, updated_at: n.toISOString() } : mem) }));
        get()._sysEvent('grace_period_expired', { member_id: m.id });
      }
    });
    // Invite expiration
    s.invites.filter(i => i.status === 'active' && new Date(i.expires_at) < n && !i.deleted_at).forEach(i => {
      set(st => ({ invites: st.invites.map(inv => inv.id === i.id ? { ...inv, status: 'expired' as const } : inv) }));
    });
    get()._persist();
  },
}));
